import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ToastContainer } from 'react-toastify';
import { TreeTable, TreeTableSelectionKeysType, TreeTableSelectionEvent } from 'primereact/treetable';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { TreeNode } from 'primereact/treenode';
import Request from '../../../shared/request/Request';
import ToasterHelper from '../../../shared/helpers/toaster.helper';
import TreeNodeHelper from '../../../shared/helpers/tree-node.helper';
import { API } from '../../../shared/constants/API';
import { HttpStatusCode } from '../../../shared/request/HttpStatusCode';
import { Form } from 'react-bootstrap';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';

function BackOfficeInterests() {
  const { t } = useTranslation();
  const [loaded, setLoaded] = useState(false);
  const [validated, setValidated] = useState(false);
  const [nodesTemp, setNodesTemp] = useState<TreeNode[]>([]);
  const [nodes, setNodes] = useState<TreeNode[]>([]);
  const [node, setNode] = useState<TreeNode>(null);
  const [selectedNodeKeys, setSelectedNodeKeys] = useState<TreeTableSelectionKeysType | string>(null);
  const [isEventModalVisible, setIsEventModalVisible] = useState(false);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(true);
  const [parentInterestDisabled, setParentInterestDisabled] = useState(false);
  const [parentInterestId, setParentInterestId] = useState(0);
  const [parentInterest, setParentInterest] = useState('');
  const [subInterest, setSubInterest] = useState('');

  const loadData = useCallback(async () => {
    Request.get(`${API.BACK_OFFICE_SERVICE}/getInterests`, {})
      .then((response: any) => {
        if (response.status.toString() === HttpStatusCode.OK) {
          setNodes(TreeNodeHelper.mapNodes(response.body.result));
          setNodesTemp(TreeNodeHelper.mapNodes(response.body.result));
        } else {
          if (response.status.toString() === HttpStatusCode.TOO_MANY_REQUESTS) {
            ToasterHelper.notify(t('ERRORS.TRY_AGAIN_LATER'), 'info');
          }
          return [];
        }
      })
      .catch(error => {
        ToasterHelper.notify(t('ERRORS.AN_ERROR_OCCURRED'), 'error');
        console.log(error);
        return [];
      });
  }, [t]);

  useEffect(() => {
    if (!loaded) {
      setLoaded(true);
      loadData();
    }
  }, [loadData, loaded]);

  const validateName = (event: any) => {
    setParentInterest('');
    if (event.target.value.length >= 2) {
      setParentInterest(event.target.value);
      setSubmitButtonDisabled(false);
    } else {
      setSubmitButtonDisabled(true);
    }
    event.preventDefault();
  };

  const changeParentInterest = (event: any) => {
    const parentInterestId = parseInt(event.target.value, 10);
    setParentInterestId(parentInterestId);
    setParentInterestDisabled(true);
    if (parentInterestId === 0) {
      setParentInterestDisabled(false);
      if (parentInterest.length < 2) {
        setSubmitButtonDisabled(false);
      }
    } else {
      setSubmitButtonDisabled(false);
    }
  };

  const onAddInterest = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    setSubmitButtonDisabled(true);

    if ((parentInterestId > 0 || parentInterest.length > 1) && subInterest.length > 1) {
      Request.post(`${API.BACK_OFFICE_SERVICE}/createInterest`, {
        body: {
          parentName: parentInterest,
          parentId: parentInterestId,
          subInterestName: subInterest
        }
      })
        .then((response: any) => {
          if (response.status.toString() === HttpStatusCode.OK) {
            setIsEventModalVisible(false);
            setSubmitButtonDisabled(true);
            setParentInterestDisabled(false);
            setParentInterestId(0);
            setParentInterest('');
            setSubInterest('');
            loadData();
          } else {
            if (response.status.toString() === HttpStatusCode.TOO_MANY_REQUESTS) {
              ToasterHelper.notify(t('ERRORS.TRY_AGAIN_LATER'), 'info');
            }
          }
        })
        .catch(error => {
          ToasterHelper.notify(t('ERRORS.AN_ERROR_OCCURRED'), 'error');
          console.log(error);
        });
    }

    setValidated(true);
  };

  const updateNodes = (localNode, localNodes, type) => {
    let newNodes = JSON.parse(JSON.stringify(localNodes));
    let editedNode = TreeNodeHelper.findNodeByKey(newNodes, localNode.key);
    editedNode.data['label'] = localNode.data['label'];
    editedNode.data['active'] = localNode.data['active'];
    editedNode.data['isActive'] = localNode.data['isActive'];

    if (type === 'nodesTemp') {
      setNodesTemp(newNodes);
    } else {
      setNodes(newNodes);
    }
  };

  const onEditorValueChange = (options, value) => {
    let localNode: TreeNode = options.node;
    localNode.data['label'] = value;
    localNode.data['active'] = options.node.data['active'];
    localNode.data['isActive'] = options.node.data['isActive'];
    setNode(localNode);

    updateNodes(localNode, nodes, 'nodes');
  };

  const updateInterest = () => {
    Request.put(`${API.BACK_OFFICE_SERVICE}/updateInterest`, {
      body: {
        id: node.key,
        name: node.data['label'],
        isActive: node.data['isActive']
      }
    })
      .then((response: any) => {
        if (response.status.toString() === HttpStatusCode.OK) {
          updateNodes(node, nodes, 'nodesTemp');
          ToasterHelper.notify(t('BACK_OFFICE.INTERESTS.INTEREST_UPDATED_SUCCESSFULLY'), 'success');
        } else {
          updateNodes(node, nodesTemp, 'nodes');
          if (response.status.toString() === HttpStatusCode.TOO_MANY_REQUESTS) {
            ToasterHelper.notify(t('ERRORS.TRY_AGAIN_LATER'), 'info');
          }
          return [];
        }
      })
      .catch(error => {
        ToasterHelper.notify(t('ERRORS.AN_ERROR_OCCURRED'), 'error');
        console.log(error);
        return [];
      });
  };

  const onEditorBlur = () => {
    setNodes(nodesTemp);
  };

  const isNodeActive = (e, options) => {
    let localNode: TreeNode = options.node;
    localNode.data['isActive'] = e.checked;
    localNode.data['active'] = e.checked ? 'True' : 'False';

    setNode(localNode);
    updateNodes(localNode, nodes, 'nodes');
  };

  const inputTextEditor = options => {
    return (
      <div className='display-contents'>
        <InputText
          type='text'
          value={options.rowData[options.field]}
          onBlur={onEditorBlur}
          onChange={e => onEditorValueChange(options, e.target.value)}
          onKeyDown={e => e.stopPropagation()}
        />
        {'   '}
        {t('BACK_OFFICE.INTERESTS.INTEREST_ACTIVE')}
        <Checkbox
          className='align-checkbox'
          onBlur={onEditorBlur}
          onChange={e => isNodeActive(e, options)}
          checked={options.node.data['isActive']}></Checkbox>
        {'   '}
        <Button
          type='button'
          className='background button-no-border button-border-radius cursor-pointer'
          icon='pi pi-save'
          severity='success'
          rounded
          onClick={updateInterest}></Button>
      </div>
    );
  };

  return (
    <div className='main-div'>
      <ToastContainer />
      <div className='main-content'>
        <div className='col-md-12 flex block'>
          <div className='col-md-10'>
            <h4>{t('BACK_OFFICE.INTERESTS.INTERESTS_LIST')}</h4>
          </div>
          <div className='col-md-2 text-right text-sm'>
            <Button
              className='background button-border-radius cursor-pointer bottom-3 max-h-10'
              onClick={() => setIsEventModalVisible(true)}>
              <b>{t('BACK_OFFICE.INTERESTS.ADD_INTEREST')}</b>
            </Button>
          </div>
        </div>

        <div className='card'>
          <TreeTable
            value={nodes}
            selectionMode='checkbox'
            selectionKeys={selectedNodeKeys}
            onSelectionChange={(e: TreeTableSelectionEvent) => setSelectedNodeKeys(e.value)}
            tableStyle={{ minWidth: '50rem' }}>
            <Column field='label' header='Name' expander editor={inputTextEditor}></Column>
            <Column field='active' header='Is Active'></Column>
          </TreeTable>
          <Dialog
            header={t('BACK_OFFICE.INTERESTS.ADD_INTEREST')}
            visible={isEventModalVisible}
            onHide={() => {
              if (!isEventModalVisible) return;
              setIsEventModalVisible(false);
            }}
            style={{ width: '75vw' }}
            breakpoints={{ '750px': '75vw', '641px': '100vw' }}>
            <Form noValidate validated={validated} onSubmit={onAddInterest}>
              <div className='col-md-12 flex text-sm'>
                <div className='col-md-2'>{t('BACK_OFFICE.INTERESTS.PARENT_INTEREST')}</div>
                <div className='col-md-10'>
                  <select
                    className='border-1 rounded-1 text-sm min-h-8 min-w-10'
                    required
                    onChange={changeParentInterest}
                    value={parentInterestId}
                    disabled={parentInterestDisabled}>
                    <option value='0' key='0' />
                    {nodes.map((node: any) => (
                      <option value={node.key} key={node.key}>
                        {node.data.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className='col-md-12 flex mt-3 text-sm'>
                <div className='col-md-2'>{t('BACK_OFFICE.INTERESTS.PARENT_INTEREST')}</div>
                <div className='col-md-10'>
                  <Form.Group className='mb-6' controlId='parentInterest'>
                    <Form.Control
                      type='text'
                      onChange={validateName}
                      placeholder={t('BACK_OFFICE.INTERESTS.PARENT_INTEREST')}
                      disabled={parentInterestDisabled}
                    />
                    <div>
                      {parentInterestId === 0 && parentInterest.length < 2 && (
                        <small className='text-danger'>{t('BACK_OFFICE.INTERESTS.VALIDATIONS.PARENT_INTEREST_REQUIRED')}</small>
                      )}
                    </div>
                  </Form.Group>
                </div>
              </div>
              <div className='col-md-12 flex text-sm'>
                <div className='col-md-2'>{t('BACK_OFFICE.INTERESTS.SUB_INTEREST')}</div>
                <div className='col-md-10'>
                  <Form.Group className='mb-6' controlId='subInterest'>
                    <Form.Control
                      type='text'
                      value={subInterest}
                      onChange={event => setSubInterest(event.target.value)}
                      placeholder={t('BACK_OFFICE.INTERESTS.SUB_INTEREST')}
                      required
                    />
                    <div>
                      {subInterest.length < 2 && (
                        <small className='text-danger'>{t('BACK_OFFICE.INTERESTS.VALIDATIONS.SUB_INTEREST_REQUIRED')}</small>
                      )}
                    </div>
                  </Form.Group>
                </div>
              </div>
              <div className='col-md-12 text-right'>
                <Button className='background button-border-radius cursor-pointer max-h-1/3' type='submit' disabled={submitButtonDisabled}>
                  <b>{t('COMMON.SUBMIT')}</b>
                </Button>
              </div>
            </Form>
          </Dialog>
        </div>
      </div>
    </div>
  );
}

export default BackOfficeInterests;
